"use client"

import * as React from "react"

export type PrismNavMode = "launcher" | "sidebar"

const STORAGE_KEY = "prism:nav-mode:v1"
const CHANGE_EVENT = "prism-nav-mode-change"

export function getPrismNavMode(): PrismNavMode {
  if (typeof window === "undefined") return "launcher"
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return stored === "sidebar" ? "sidebar" : "launcher"
}

export function setPrismNavMode(mode: PrismNavMode): void {
  if (typeof window === "undefined") return
  window.localStorage.setItem(STORAGE_KEY, mode)
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

export function usePrismNavMode(): [PrismNavMode, (mode: PrismNavMode) => void] {
  const [mode, setModeState] = React.useState<PrismNavMode>(() => getPrismNavMode())

  React.useEffect(() => {
    const sync = () => setModeState(getPrismNavMode())
    window.addEventListener(CHANGE_EVENT, sync)
    return () => window.removeEventListener(CHANGE_EVENT, sync)
  }, [])

  const setMode = React.useCallback((next: PrismNavMode) => {
    setPrismNavMode(next)
    setModeState(next)
  }, [])

  return [mode, setMode]
}

export function subscribePrismNavMode(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {}
  window.addEventListener(CHANGE_EVENT, onChange)
  return () => window.removeEventListener(CHANGE_EVENT, onChange)
}
